import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const TOKEN_KEYS = [
  "background",
  "foreground",
  "muted",
  "muted_foreground",
  "accent",
  "accent_foreground",
  "gold",
  "forest",
  "border",
] as const;

export type ThemeTokenKey = (typeof TOKEN_KEYS)[number];
export type ThemeTokens = Record<ThemeTokenKey, string>;

export const DEFAULT_TOKENS: ThemeTokens = {
  background: "oklch(0.04 0.005 60)",
  foreground: "oklch(0.98 0.005 80)",
  muted: "oklch(0.18 0.01 60)",
  muted_foreground: "oklch(0.72 0.015 80)",
  accent: "oklch(0.78 0.12 80)",
  accent_foreground: "oklch(0.08 0.01 60)",
  gold: "oklch(0.82 0.13 82)",
  forest: "oklch(0.32 0.05 155)",
  border: "rgba(255,255,255,0.12)",
};

export type ThemePreset = {
  id: string;
  name: string;
  is_active: boolean;
  heading_font: string;
  body_font: string;
  tokens: ThemeTokens;
};

const COLS = "id,name,is_active,heading_font,body_font,tokens";

function normalize(row: any): ThemePreset {
  return {
    id: row.id,
    name: row.name,
    is_active: !!row.is_active,
    heading_font: row.heading_font ?? "Cormorant Garamond",
    body_font: row.body_font ?? "Inter",
    tokens: { ...DEFAULT_TOKENS, ...(row.tokens ?? {}) },
  };
}

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

export const getActiveTheme = createServerFn({ method: "GET" }).handler(
  async (): Promise<ThemePreset> => {
    const sb = publicClient();
    const { data } = await sb
      .from("theme_presets")
      .select(COLS)
      .eq("is_active", true)
      .maybeSingle();
    if (data) return normalize(data);
    return {
      id: "default",
      name: "Default",
      is_active: true,
      heading_font: "Cormorant Garamond",
      body_font: "Inter",
      tokens: DEFAULT_TOKENS,
    };
  },
);

export const listThemePresets = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ThemePreset[]> => {
    const { data, error } = await context.supabase
      .from("theme_presets")
      .select(COLS)
      .order("name", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []).map(normalize);
  });

const addSchema = z.object({ name: z.string().trim().min(1).max(80) });
export const addThemePreset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => addSchema.parse(i))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    // Clone from active
    const { data: active } = await sb
      .from("theme_presets")
      .select(COLS)
      .eq("is_active", true)
      .maybeSingle();
    const seed = active ? normalize(active) : null;
    const { data: row, error } = await sb
      .from("theme_presets")
      .insert({
        name: data.name,
        is_active: false,
        heading_font: seed?.heading_font ?? "Cormorant Garamond",
        body_font: seed?.body_font ?? "Inter",
        tokens: seed?.tokens ?? DEFAULT_TOKENS,
      })
      .select(COLS)
      .single();
    if (error) throw new Error(error.message);
    return normalize(row);
  });

const tokenSchema = z.record(z.enum(TOKEN_KEYS), z.string().min(1).max(80));
const updateSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(80).optional(),
  heading_font: z.string().trim().min(1).max(80).optional(),
  body_font: z.string().trim().min(1).max(80).optional(),
  tokens: tokenSchema.optional(),
});
export const updateThemePreset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => updateSchema.parse(i))
  .handler(async ({ data, context }) => {
    const { id, tokens, ...rest } = data;
    const patch: {
      name?: string;
      heading_font?: string;
      body_font?: string;
      tokens?: ThemeTokens;
    } = { ...rest };
    if (tokens) {
      // Merge with existing tokens so partial edits don't blow away other keys.
      const { data: cur } = await context.supabase
        .from("theme_presets")
        .select("tokens")
        .eq("id", id)
        .single();
      const existing = (cur?.tokens ?? {}) as Partial<ThemeTokens>;
      patch.tokens = { ...DEFAULT_TOKENS, ...existing, ...tokens } as ThemeTokens;
    }
    const { error } = await context.supabase
      .from("theme_presets")
      .update(patch)
      .eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const idSchema = z.object({ id: z.string().uuid() });
export const deleteThemePreset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => idSchema.parse(i))
  .handler(async ({ data, context }) => {
    // Don't allow deleting the active preset
    const { data: row } = await context.supabase
      .from("theme_presets")
      .select("is_active")
      .eq("id", data.id)
      .single();
    if (row?.is_active) throw new Error("Cannot delete the active theme. Activate another first.");
    const { error } = await context.supabase
      .from("theme_presets")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const setActiveTheme = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => idSchema.parse(i))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    // Two-step swap to satisfy the partial unique index.
    const { error: e1 } = await sb
      .from("theme_presets")
      .update({ is_active: false })
      .eq("is_active", true);
    if (e1) throw new Error(e1.message);
    const { error: e2 } = await sb
      .from("theme_presets")
      .update({ is_active: true })
      .eq("id", data.id);
    if (e2) throw new Error(e2.message);
    return { ok: true };
  });
